/**
 * Performance analysis — pre-flight check that can override the user's chosen
 * template with a focused intervention plan when a node is critically struggling.
 *
 * All functions are pure: no I/O, no side effects.
 */

import { type PlanBuildData, type PlanConfig, type PlanTemplate } from "./plan-template.js";
import { strengthenStop } from "./stop-builders/strengthen.js";
import { reviewStop } from "./stop-builders/review.js";

// ─── types ────────────────────────────────────────────────────────────────────

export interface PerformanceAnalysis {
  interventionNeeded: boolean;
  trigger: "high-lapse" | "none";
  interventionNodeId: string | null;
  interventionNodeTitle: string | null;
  lapseRate: number;
}

// ─── analyzePerformance ───────────────────────────────────────────────────────

/**
 * Scan template nodes for a critical high-lapse situation.
 *
 * Fires when a node that is unlocked + engaged has:
 *   - 7-day lapse rate ≥ config.interventionLapseThreshold (default 40%)
 *   - total attempts in that window ≥ config.interventionMinAttempts (default 15)
 *
 * Returns the worst-offending node if the condition is met; otherwise a no-op result.
 */
export function analyzePerformance(
  data: PlanBuildData,
  config: PlanConfig,
): PerformanceAnalysis {
  const cutoff = new Date(data.now.getTime() - config.lapseWindowMs);
  let worstNodeId: string | null = null;
  let worstNodeTitle: string | null = null;
  let worstRate = 0;

  for (const node of data.nodes) {
    if (!data.unlockedNodeSet.has(node.id) || !data.engagedNodeSet.has(node.id)) continue;
    const nodeItemIds = new Set(
      data.items.filter((i) => i.nodeId === node.id).map((i) => i.id),
    );
    if (nodeItemIds.size === 0) continue;
    const recent = data.reviews.filter(
      (r) => nodeItemIds.has(r.itemId) && new Date(r.timestamp) >= cutoff,
    );
    if (recent.length < config.interventionMinAttempts) continue;
    const lapses = recent.filter((r) => !r.correct).length;
    const rate = lapses / recent.length;
    if (rate >= config.interventionLapseThreshold && rate > worstRate) {
      worstNodeId = node.id;
      worstNodeTitle = node.title;
      worstRate = rate;
    }
  }

  if (worstNodeId !== null) {
    return {
      interventionNeeded: true,
      trigger: "high-lapse",
      interventionNodeId: worstNodeId,
      interventionNodeTitle: worstNodeTitle,
      lapseRate: worstRate,
    };
  }
  return {
    interventionNeeded: false,
    trigger: "none",
    interventionNodeId: null,
    interventionNodeTitle: null,
    lapseRate: 0,
  };
}

// ─── resolveTemplate ──────────────────────────────────────────────────────────

/**
 * Intercept the user's chosen template and, if intervention is warranted,
 * return a one-off Focus Session template instead:
 *   - Drops the Introduce stop (no new material during crisis)
 *   - Keeps the Review stop (FSRS due dates are always honored)
 *   - Expands the Strengthen budget to absorb the freed Introduce cap
 *
 * Re-evaluates on every plan call — clears automatically once recall recovers.
 */
export function resolveTemplate(
  userTemplate: PlanTemplate,
  data: PlanBuildData,
): {
  resolvedTemplate: PlanTemplate;
  mode: "standard" | "intervention";
  interventionNodeTitle: string | undefined;
} {
  const analysis = analyzePerformance(data, userTemplate.config);
  if (!analysis.interventionNeeded) {
    return { resolvedTemplate: userTemplate, mode: "standard", interventionNodeTitle: undefined };
  }
  const interventionTemplate: PlanTemplate = {
    ...userTemplate,
    id: "__intervention__",
    label: "Focus Session",
    description: "",
    stops: [strengthenStop, reviewStop],
    config: {
      ...userTemplate.config,
      warmupItemCap: userTemplate.config.warmupItemCap + userTemplate.config.newItemCap,
    },
  };
  return {
    resolvedTemplate: interventionTemplate,
    mode: "intervention",
    interventionNodeTitle: analysis.interventionNodeTitle ?? undefined,
  };
}
